/**
 * TasksPage — Cola diaria de tareas (siguiente acción por lead según reglas).
 * Modo foco: ejecuta una tras otra (llamada hecha / enviar mensaje) + posponer.
 */
import React, { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  ListTodo, Phone, MessageSquare, Check, Clock, AlertTriangle, MapPin,
  Play, ChevronRight, PartyPopper, X, SkipForward, Send, Sparkles, User, CheckCircle2,
  CalendarRange, Archive,
} from "lucide-react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { LogActivityDialog } from "@/components/LogActivityDialog";
import { RichTextEditor } from "@/components/RichTextEditor";
import { SendSmsDialog } from "@/components/SendSmsDialog";
import { FEATURES } from "@/lib/features";
import { useAuth } from "@/context/AuthContext";
import { htmlToText } from "@/lib/html";
import { formatTravelDates } from "@/lib/dates";

const initials = (n = "") => n.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
const relDue = (iso) => { try { return format(new Date(iso), "d MMM · HH:mm", { locale: es }); } catch { return "—"; } };
const daysOverdue = (iso) => {
  try { const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000); return d > 0 ? d : 0; } catch { return 0; }
};
const overdueSeverity = (d) =>
  d > 30 ? "bg-[#C45A45] text-white"
    : d >= 7 ? "bg-[#FCEEEA] text-[#803D26] border border-[#C45A45]/30"
      : "bg-[#F1E9DA] text-[#6B4F20] border border-[#C28F5B]/30";

const PRIORITY = {
  high: { label: "Alta", cls: "border-[#C45A45]/30 bg-[#FCEEEA] text-[#803D26]" },
  medium: { label: "Media", cls: "border-[#C28F5B]/30 bg-[#F1E9DA] text-[#6B4F20]" },
  low: { label: "Baja", cls: "border-black/10 bg-secondary text-foreground" },
};

function ChannelBadge({ channel }) {
  return channel === "call" ? (
    <Badge variant="outline" className="gap-1 border-[#34406B]/30 bg-[#E5E8F2] text-[#34406B]">
      <Phone className="h-3 w-3" /> Llamada
    </Badge>
  ) : (
    <Badge variant="outline" className="gap-1 border-[#235858]/30 bg-[#E0EBEB] text-[#235858]">
      <MessageSquare className="h-3 w-3" /> Mensaje
    </Badge>
  );
}

function ArchiveButton({ onConfirm, disabled, iconOnly = false, className = "", testid = "task-archive-trigger" }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        {iconOnly ? (
          <Button
            size="icon"
            variant="ghost"
            className={`h-8 w-8 text-muted-foreground hover:text-[#803D26] ${className}`}
            disabled={disabled}
            title="Archivar tarea"
            data-testid={testid}
          >
            <Archive className="h-4 w-4" />
          </Button>
        ) : (
          <Button size="sm" variant="outline" className={`gap-1 ${className}`} disabled={disabled} data-testid={testid}>
            <Archive className="h-3.5 w-3.5" /> Archivar
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="task-archive-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Archivar tarea</AlertDialogTitle>
          <AlertDialogDescription>
            Esta tarea dejará de aparecer en tu lista de Tareas. No se envía nada a Zicasso; solo se oculta aquí. El lead seguirá disponible en el tablero.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="task-archive-cancel">Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid="task-archive-confirm">Archivar</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function TaskRow({ task, onOpen, onArchive }) {
  const d = daysOverdue(task.reminder_due_at);
  const travel = formatTravelDates(task.departure_date, task.return_date);
  return (
    <div
      className={`flex items-center gap-4 rounded-xl border bg-card p-4 shadow-sm transition-all hover:shadow-md ${task.is_overdue ? "border-[#C45A45]/30 border-l-4 border-l-[#C45A45]" : "border-black/5"}`}
      data-testid={`task-row-${task.id}`}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
        {initials(task.traveler_name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-semibold">{task.traveler_name}</span>
          {travel && (
            <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-foreground/70" data-testid={`task-travel-dates-${task.id}`}>
              <CalendarRange className="h-3 w-3" />{travel}
            </span>
          )}
          {task.is_overdue && <Badge variant="destructive" className="h-4 gap-1 text-[9px]"><AlertTriangle className="h-2.5 w-2.5" />Atrasada {d}d</Badge>}
          {task.has_unread && <span className="h-2 w-2 rounded-full bg-[#C45A45]" title="Sin leer" />}
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
          <MapPin className="h-3 w-3" />{(task.countries || []).join(", ")}
          <span className="text-muted-foreground/40">·</span>
          <User className="h-3 w-3" />{task.assigned_zc_agent_name}
        </div>
        <div className="mt-1 text-xs font-medium text-foreground/80">{task.timeline_step_label}</div>
      </div>
      <div className="hidden shrink-0 flex-col items-end gap-1.5 sm:flex">
        <ChannelBadge channel={task.channel} />
        {task.is_overdue ? (
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${overdueSeverity(d)}`}
            title={`Vencía el ${relDue(task.reminder_due_at)}`}
            data-testid={`task-overdue-days-${task.id}`}
          >
            Vencido hace {d} {d === 1 ? "día" : "días"}
          </span>
        ) : (
          <span className="text-[10px] text-muted-foreground">Vence {relDue(task.reminder_due_at)}</span>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <ArchiveButton onConfirm={() => onArchive?.(task)} iconOnly testid={`task-archive-row-${task.id}`} />
        <Button size="sm" onClick={() => onOpen(task)} data-testid={`task-open-${task.id}`}>
          Abrir <ChevronRight className="ml-1 h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

function FocusDialog({ open, onClose, initialTask, onRefetch, agentFilter }) {
  const qc = useQueryClient();
  const editorRef = useRef(null);
  const [task, setTask] = useState(initialTask);
  const [body, setBody] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [subject, setSubject] = useState("");
  const [completedCount, setCompletedCount] = useState(0);
  const [showCtx, setShowCtx] = useState(false);
  const [aiInstructions, setAiInstructions] = useState("");
  const [aiInfo, setAiInfo] = useState("");
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [snoozeAt, setSnoozeAt] = useState("");
  const [snoozeNote, setSnoozeNote] = useState("");
  const [logOpen, setLogOpen] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);

  const agentQ = agentFilter && agentFilter !== "__all__" ? `?agent_id=${agentFilter}` : "";

  const ctx = useQuery({
    queryKey: ["zicasso-lead", task?.lead_id],
    queryFn: async () => (await api.get(`/zicasso/leads/${task.lead_id}`)).data,
    enabled: open && !!task?.lead_id,
  });

  React.useEffect(() => {
    if (open) {
      setTask(initialTask);
      setCompletedCount(0);
    }
  }, [open, initialTask]);

  React.useEffect(() => {
    setAiInstructions("");
    setAiInfo("");
    if (task?.channel === "message") {
      setSubject(task.draft_subject || "");
    } else {
      setBody("");
      setBodyHtml("");
      setSubject("");
    }
  }, [task]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["tasks-today"] });
    qc.invalidateQueries({ queryKey: ["tasks-summary"] });
    qc.invalidateQueries({ queryKey: ["zicasso-stats"] });
    qc.invalidateQueries({ queryKey: ["zicasso-leads"] });
  };

  const advance = (res) => {
    setCompletedCount((c) => c + 1);
    invalidate();
    if (res?.next) {
      setTask(res.next);
    } else {
      setTask(null); // cola completada
    }
  };

  const completeMut = useMutation({
    mutationFn: (payload) => api.post(`/zicasso/tasks/${task.id}/complete${agentQ}`, payload),
    onSuccess: (r) => {
      toast.success(task.channel === "call" ? "Llamada registrada" : (r.data?.demo ? "Mensaje registrado (demo)" : "Mensaje enviado"));
      advance(r.data);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al completar"),
  });

  const skipMut = useMutation({
    mutationFn: (payload) => api.post(`/zicasso/tasks/${task.id}/skip${agentQ}`, payload),
    onSuccess: (r) => {
      toast.success("Tarea pospuesta");
      setSnoozeOpen(false); setSnoozeAt(""); setSnoozeNote("");
      advance(r.data);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo posponer"),
  });

  const archiveMut = useMutation({
    mutationFn: () => api.post(`/zicasso/tasks/${task.id}/archive${agentQ}`),
    onSuccess: (r) => {
      toast.success("Tarea archivada");
      advance(r.data);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo archivar"),
  });

  const aiMut = useMutation({
    mutationFn: () => api.post(`/zicasso/leads/${task.lead_id}/ai-draft`, { instructions: aiInstructions || null }),
    onSuccess: (r) => {
      editorRef.current?.setText(r.data.text || "");
      if (r.data.subject) setSubject(r.data.subject);
      if (r.data.source === "template_fallback") {
        setAiInfo("Plantilla (IA no disponible)");
        toast.warning(r.data.warning || "IA no disponible; se usó plantilla");
      } else {
        setAiInfo(r.data.used_template ? `IA · basada en ${r.data.used_template}` : "Generado con IA");
        toast.success("Borrador generado con IA");
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo generar con IA"),
  });

  const p = task && (PRIORITY[task.priority] || PRIORITY.low);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); onRefetch?.(); } }}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="task-focus-dialog">
        <DialogTitle className="sr-only">Modo foco de tareas</DialogTitle>
        <DialogDescription className="sr-only">Ejecuta la siguiente tarea del lead y avanza automáticamente a la siguiente.</DialogDescription>

        {!task ? (
          <div className="flex flex-col items-center justify-center px-8 py-16 text-center" data-testid="task-focus-done">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#E2EBE5] text-[#244532]">
              <PartyPopper className="h-8 w-8" />
            </div>
            <h2 className="font-heading text-2xl font-semibold">¡Cola completada!</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Has procesado {completedCount} tarea{completedCount !== 1 ? "s" : ""} en esta sesión. No quedan tareas pendientes.
            </p>
            <Button className="mt-6" onClick={() => { onClose(); onRefetch?.(); }} data-testid="focus-close-done">Cerrar</Button>
          </div>
        ) : (
          <div className="flex flex-col">
            {/* header */}
            <div className="flex items-start justify-between gap-3 border-b bg-secondary/30 p-5">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                  {initials(task.traveler_name)}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="truncate font-heading text-lg font-semibold" data-testid="focus-lead-name">{task.traveler_name}</h2>
                    {task.is_overdue && <Badge variant="destructive" className="h-4 gap-1 text-[9px]"><AlertTriangle className="h-2.5 w-2.5" />Atrasada</Badge>}
                  </div>
                  <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                    <MapPin className="h-3 w-3" />{(task.countries || []).join(", ")}
                    <span className="text-muted-foreground/40">·</span>{task.assigned_zc_agent_name}
                    {formatTravelDates(task.departure_date, task.return_date) && (
                      <>
                        <span className="text-muted-foreground/40">·</span>
                        <span className="inline-flex items-center gap-1 font-medium text-foreground/70" data-testid="focus-travel-dates">
                          <CalendarRange className="h-3 w-3" />{formatTravelDates(task.departure_date, task.return_date)}
                        </span>
                      </>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <ChannelBadge channel={task.channel} />
                <Badge variant="outline" className={`h-4 text-[9px] ${p.cls}`}>Prioridad {p.label}</Badge>
              </div>
            </div>

            {/* body */}
            <div className="thin-scroll max-h-[52vh] overflow-y-auto p-5">
              {ctx.data && (ctx.data.client_brief || (ctx.data.messages || []).length > 0) && (
                <div className="mb-3 rounded-lg border bg-card" data-testid="focus-context">
                  <button
                    onClick={() => setShowCtx((s) => !s)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left"
                    data-testid="focus-context-toggle"
                  >
                    <span className="flex items-center gap-1.5 text-xs font-semibold">
                      <MessageSquare className="h-3.5 w-3.5" /> Contexto del cliente — brief + conversación
                    </span>
                    <ChevronRight className={`h-4 w-4 transition-transform ${showCtx ? "rotate-90" : ""}`} />
                  </button>
                  {showCtx && (
                    <div className="space-y-3 border-t p-3">
                      {ctx.data.client_brief && (
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Brief del cliente</div>
                          <p className="thin-scroll mt-0.5 max-h-32 overflow-y-auto whitespace-pre-wrap text-xs text-muted-foreground">{ctx.data.client_brief}</p>
                        </div>
                      )}
                      {(ctx.data.messages || []).length > 0 && (
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Últimos mensajes ({ctx.data.messages_count})</div>
                          <div className="mt-1 space-y-1">
                            {(ctx.data.messages || []).slice(-3).map((m) => (
                              <div key={m.id} className="rounded border bg-secondary/40 p-1.5 text-[11px]">
                                <div className="flex items-center justify-between">
                                  <span className="font-semibold">{m.sender_type === "TRAVELER" ? "Cliente" : (m.sender_name || "Agente")}</span>
                                  <span className="text-[10px] text-muted-foreground">{relDue(m.sent_at)}</span>
                                </div>
                                <span className="text-muted-foreground">{htmlToText(m.body).slice(0, 140) || "(sin texto)"}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="mb-4 rounded-lg border-l-4 border-primary bg-primary/5 p-3">
                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-primary">
                  <Sparkles className="h-3 w-3" /> Siguiente acción (manual de buenas prácticas)
                </div>
                <div className="mt-1 text-sm font-semibold">{task.timeline_step_label}</div>
                {task.hint && <div className="mt-1 text-xs text-muted-foreground">{task.hint}</div>}
              </div>

              {task.channel === "call" ? (
                <div className="rounded-lg border bg-card p-4 text-sm" data-testid="focus-call-panel">
                  <div className="flex items-center gap-2 font-semibold"><Phone className="h-4 w-4 text-[#34406B]" /> Realiza la llamada</div>
                  <p className="mt-2 text-muted-foreground">
                    Llama al cliente y deja voicemail si no contesta. Combina siempre llamada + mensaje de seguimiento.
                    Cuando termines, márcala como hecha para avanzar al siguiente paso del timeline.
                  </p>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/25 bg-primary/5 p-2.5" data-testid="focus-call-sms">
                    <span className="text-[11px] text-primary">¿No contesta? Envíale un SMS de seguimiento</span>
                    <Button size="sm" variant="outline" onClick={() => setSmsOpen(true)} data-testid="focus-call-sms-button">
                      <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Enviar SMS
                    </Button>
                  </div>
                  {FEATURES.whatsapp && (
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#25D366]/30 bg-[#25D366]/5 p-2.5" data-testid="focus-call-whatsapp">
                      <span className="text-[11px] text-[#128C4B]">¿No contesta? Envíale un WhatsApp de seguimiento</span>
                      <WhatsAppButton lead={ctx.data} showLog={false} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2" data-testid="focus-message-panel">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-semibold">Mensaje a enviar</span>
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={() => aiMut.mutate()} disabled={aiMut.isPending} data-testid="focus-ai-draft-button">
                        <Sparkles className={`mr-1 h-3 w-3 ${aiMut.isPending ? "animate-pulse" : ""}`} />
                        {aiMut.isPending ? "Generando…" : "Generar con IA"}
                      </Button>
                      {task.draft_templates?.length > 0 && (
                        <span className="text-[10px] text-muted-foreground">Plantillas: <strong>{task.draft_templates.join(" + ")}</strong></span>
                      )}
                    </div>
                  </div>
                  <Input
                    value={aiInstructions}
                    onChange={(e) => setAiInstructions(e.target.value)}
                    placeholder='Instrucción para la IA (opcional): "ofrece una llamada", "menciona el presupuesto"…'
                    className="text-xs"
                    data-testid="focus-ai-instructions"
                  />
                  {aiInfo ? (
                    <p className="flex items-center gap-1 text-[11px] font-medium text-primary" data-testid="focus-draft-source">
                      <Sparkles className="h-3 w-3" /> {aiInfo}
                    </p>
                  ) : (
                    <p className="rounded-md bg-[#E0EBEB]/60 px-2.5 py-1.5 text-[11px] text-[#235858]">
                      <Sparkles className="mr-1 inline h-3 w-3" />
                      Texto de plantilla con los datos de <strong>{task.traveler_name}</strong>. Pulsa "Generar con IA" para personalizarlo con la conversación y el brief.
                    </p>
                  )}
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (opcional)" data-testid="focus-subject-input" />
                  <RichTextEditor
                    key={task.id}
                    ref={editorRef}
                    initialText={task.draft_text || (task.suggested_template ? htmlToText(task.suggested_template.body) : "")}
                    placeholder="Escribe el mensaje… (usa la barra para negrita, enlaces, listas)"
                    testid="focus-body-editor"
                    minHeight={180}
                    onChange={({ html, text }) => { setBody(text); setBodyHtml(html); }}
                  />
                  {FEATURES.whatsapp && (
                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#25D366]/30 bg-[#25D366]/5 p-2.5" data-testid="focus-whatsapp-block">
                      <span className="flex items-center gap-1.5 text-[11px] text-[#128C4B]">
                        <MessageSquare className="h-3.5 w-3.5" /> Enviar por WhatsApp desde el número de empresa
                      </span>
                      <WhatsAppButton lead={ctx.data} text={body} onLogged={() => { invalidate(); ctx.refetch?.(); }} />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* actions */}
            <div className="flex flex-wrap items-center justify-between gap-2 border-t bg-secondary/20 p-4">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] text-muted-foreground">Posponer:</span>
                {[1, 3, 7].map((d) => (
                  <Button key={d} size="sm" variant="outline" onClick={() => skipMut.mutate({ days: d })} disabled={skipMut.isPending || completeMut.isPending} data-testid={`focus-snooze-${d}`}>
                    {d}d
                  </Button>
                ))}
                <Popover open={snoozeOpen} onOpenChange={setSnoozeOpen}>
                  <PopoverTrigger asChild>
                    <Button size="sm" variant="outline" className="gap-1" disabled={skipMut.isPending || completeMut.isPending} data-testid="focus-snooze-date-trigger">
                      <Clock className="h-3.5 w-3.5" /> A fecha…
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-80 space-y-3" data-testid="focus-snooze-popover">
                    <div>
                      <p className="text-sm font-semibold">Posponer a fecha y hora</p>
                      <p className="text-[11px] text-muted-foreground">La tarea reaparecerá y recibirás un aviso (in-app + email) a esa hora.</p>
                    </div>
                    <div>
                      <label className="text-[11px] font-medium text-muted-foreground">Fecha y hora</label>
                      <Input type="datetime-local" value={snoozeAt} onChange={(e) => setSnoozeAt(e.target.value)} className="mt-1" data-testid="focus-snooze-datetime" />
                    </div>
                    <div>
                      <label className="text-[11px] font-medium text-muted-foreground">Nota (opcional)</label>
                      <Textarea value={snoozeNote} onChange={(e) => setSnoozeNote(e.target.value)} rows={3} placeholder="Ej: Llamar para confirmar fechas y presupuesto…" className="mt-1 text-xs" data-testid="focus-snooze-note" />
                    </div>
                    <Button
                      size="sm"
                      className="w-full"
                      onClick={() => skipMut.mutate({ until: snoozeAt, note: snoozeNote })}
                      disabled={!snoozeAt || skipMut.isPending}
                      data-testid="focus-snooze-date-confirm"
                    >
                      {skipMut.isPending ? "Posponiendo…" : "Posponer y avisarme"}
                    </Button>
                  </PopoverContent>
                </Popover>
                <span className="mx-1 hidden h-4 w-px bg-black/10 sm:block" />
                <ArchiveButton onConfirm={() => archiveMut.mutate()} disabled={archiveMut.isPending || skipMut.isPending || completeMut.isPending} testid="focus-archive-trigger" />
              </div>
              <div className="flex items-center gap-2">
                {FEATURES.sms && (
                  <Button variant="outline" onClick={() => setSmsOpen(true)} data-testid="focus-sms-button">
                    <MessageSquare className="mr-1.5 h-4 w-4" /> SMS
                  </Button>
                )}
                {task.channel === "call" ? (
                  <Button onClick={() => setLogOpen(true)} disabled={completeMut.isPending} data-testid="focus-complete-call">
                    <Phone className="mr-1.5 h-4 w-4" /> Registrar llamada / SMS
                  </Button>
                ) : (
                  <Button onClick={() => completeMut.mutate({ action: "message", body, body_html: bodyHtml || null, subject: subject || null })} disabled={completeMut.isPending || !body.trim()} data-testid="focus-complete-message">
                    <Send className="mr-1.5 h-4 w-4" /> Enviar y completar
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
        {task && (
          <LogActivityDialog
            open={logOpen}
            onOpenChange={setLogOpen}
            lead={ctx.data || { id: task.lead_id }}
            taskId={task.id}
            agentId={agentFilter}
            defaultKind="answered"
            onLogged={(data) => advance(data)}
          />
        )}
        {task && (
          <SendSmsDialog
            open={smsOpen}
            onOpenChange={setSmsOpen}
            lead={ctx.data || { id: task.lead_id, traveler_name: task.traveler_name }}
            taskId={task.id}
            agentId={agentFilter}
            onCompleted={(data) => advance(data)}
            onSent={() => { invalidate(); ctx.refetch?.(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function TasksPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [agentFilter, setAgentFilter] = useState("__all__");
  const [filterDefaulted, setFilterDefaulted] = useState(false);
  const [catFilter, setCatFilter] = useState("all"); // all | call | message
  const [focusOpen, setFocusOpen] = useState(false);
  const [focusTask, setFocusTask] = useState(null);
  const [showDone, setShowDone] = useState(false);

  const agents = useQuery({
    queryKey: ["zicasso-agents"],
    queryFn: async () => (await api.get("/zicasso/agents")).data,
  });

  // Por defecto, un agente ve solo SUS tareas (el admin ve todas). Puede cambiarlo.
  useEffect(() => {
    if (filterDefaulted || !user || user.is_admin) return;
    const list = agents.data || [];
    if (!list.length) return;
    const norm = (s) => (s || "").trim().toLowerCase();
    const mine = list.find((a) => norm(a.full_name) === norm(user.full_name))
      || list.find((a) => a.email && norm(a.email) === norm(user.zc_agent_email || user.email));
    if (mine) setAgentFilter(mine.id);
    setFilterDefaulted(true);
  }, [agents.data, user, filterDefaulted]);

  const tasksQ = useQuery({
    queryKey: ["tasks-today", agentFilter],
    queryFn: async () => {
      const p = agentFilter !== "__all__" ? `?agent_id=${agentFilter}` : "";
      return (await api.get(`/zicasso/tasks/today${p}`)).data;
    },
  });

  const archiveMut = useMutation({
    mutationFn: (taskId) => api.post(`/zicasso/tasks/${taskId}/archive`),
    onSuccess: () => {
      toast.success("Tarea archivada");
      qc.invalidateQueries({ queryKey: ["tasks-today"] });
      qc.invalidateQueries({ queryKey: ["tasks-summary"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo archivar"),
  });

  const data = tasksQ.data;
  const pending = data?.pending || [];
  const done = data?.done || [];
  const skipped = data?.skipped || [];
  const progress = data?.progress || { pending: 0, done: 0, skipped: 0, total: 0 };
  const pct = progress.total ? Math.round(((progress.done + progress.skipped) / progress.total) * 100) : 0;

  const startFocus = (task) => { setFocusTask(task || overduePending[0] || pending[0] || null); setFocusOpen(true); };
  const callCount = pending.filter((t) => t.channel === "call").length;
  const msgCount = pending.length - callCount;
  const overduePending = pending.filter((t) => t.is_overdue);
  const upcomingPending = pending.filter((t) => !t.is_overdue);
  const catMatch = (t) => catFilter === "all" || (catFilter === "call" ? t.channel === "call" : t.channel !== "call");

  return (
    <div className="mx-auto max-w-[1100px] px-6 py-6" data-testid="tasks-page">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-3xl font-semibold tracking-tight">
            <ListTodo className="h-7 w-7 text-primary" /> Tareas de hoy
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Espejo de los recordatorios de Zicasso · <strong className="text-[#C45A45]">{overduePending.length}</strong> vencidos · {data?.date || ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={agentFilter} onValueChange={setAgentFilter}>
            <SelectTrigger className="w-[180px]" data-testid="tasks-agent-filter">
              <SelectValue placeholder="Todos los agentes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos los agentes</SelectItem>
              {(agents.data || []).map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => startFocus(null)} disabled={pending.length === 0} data-testid="start-focus-button">
            <Play className="mr-1.5 h-4 w-4" /> Modo foco ({pending.length})
          </Button>
        </div>
      </div>

      {/* progress */}
      <div className="mb-6 rounded-xl border border-black/5 bg-card p-5 shadow-sm">
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="font-semibold">Progreso del día</span>
          <span className="text-muted-foreground">
            <strong className="text-[#244532]">{progress.done}</strong> hechas ·{" "}
            <strong className="text-[#6B4F20]">{progress.skipped}</strong> pospuestas ·{" "}
            <strong className="text-[#C45A45]">{progress.pending}</strong> pendientes
          </span>
        </div>
        <Progress value={pct} className="h-2.5" data-testid="tasks-progress-bar" />
      </div>

      {/* filtro de categoría (arriba) */}
      {pending.length > 0 && (
        <div className="mb-4 flex w-fit items-center gap-1 rounded-lg border bg-card p-1" data-testid="tasks-category-filter">
          {[
            { key: "all", label: "Todas", count: pending.length, icon: null },
            { key: "call", label: "Llamadas", count: callCount, icon: Phone },
            { key: "message", label: "Mensajes", count: msgCount, icon: MessageSquare },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => setCatFilter(opt.key)}
              className={`flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors ${
                catFilter === opt.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`tasks-cat-${opt.key}`}
            >
              {opt.icon && <opt.icon className="h-3.5 w-3.5" />}
              {opt.label}
              <span className={`rounded-full px-1.5 text-[10px] font-semibold ${catFilter === opt.key ? "bg-white/20" : "bg-secondary text-muted-foreground"}`}>
                {opt.count}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* pending list — Vencidos / Próximos (espejo del feed de Zicasso) */}
      {tasksQ.isLoading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}</div>
      ) : pending.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-card p-16 text-center" data-testid="tasks-empty">
          <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-[#244532]" />
          <p className="font-heading text-lg font-semibold">¡Todo hecho por hoy!</p>
          <p className="mt-1 text-sm text-muted-foreground">No quedan tareas pendientes.</p>
        </div>
      ) : (
        <div className="space-y-8" data-testid="tasks-pending-list">
          {[
            { key: "overdue", label: "Vencidos", icon: AlertTriangle, tone: "text-[#C45A45]", items: overduePending.filter(catMatch) },
            { key: "upcoming", label: "Próximos", icon: Clock, tone: "text-[#6B4F20]", items: upcomingPending.filter(catMatch) },
          ].map((section) => section.items.length === 0 ? null : (
            <div key={section.key} data-testid={`tasks-section-${section.key}`}>
              <div className="mb-3 flex items-center gap-2">
                <section.icon className={`h-5 w-5 ${section.tone}`} />
                <h2 className={`font-heading text-lg font-semibold ${section.tone}`}>{section.label}</h2>
                <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-bold text-muted-foreground" data-testid={`tasks-section-count-${section.key}`}>
                  {section.items.length}
                </span>
              </div>
              <div className="space-y-3">
                {section.items.map((t) => <TaskRow key={t.id} task={t} onOpen={startFocus} onArchive={(task) => archiveMut.mutate(task.id)} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* done / skipped */}
      {(done.length > 0 || skipped.length > 0) && (
        <div className="mt-6">
          <button
            onClick={() => setShowDone((s) => !s)}
            className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
            data-testid="toggle-done-tasks"
          >
            <Check className="h-4 w-4" /> Completadas / pospuestas hoy ({done.length + skipped.length})
            <ChevronRight className={`h-4 w-4 transition-transform ${showDone ? "rotate-90" : ""}`} />
          </button>
          {showDone && (
            <div className="space-y-2" data-testid="tasks-done-list">
              {[...done, ...skipped].map((t) => (
                <div key={t.id} className="flex items-center gap-3 rounded-lg border border-black/5 bg-secondary/40 px-4 py-2.5 text-sm">
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full ${t.status === "done" ? "bg-[#E2EBE5] text-[#244532]" : "bg-[#F1E9DA] text-[#6B4F20]"}`}>
                    {t.status === "done" ? <Check className="h-3 w-3" /> : <SkipForward className="h-3 w-3" />}
                  </span>
                  <span className="flex-1 truncate">{t.traveler_name}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {t.status === "done" ? (t.action_done === "call" ? "Llamada hecha" : "Mensaje enviado") : `Pospuesta ${t.snoozed_days || 3}d`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <FocusDialog
        open={focusOpen}
        initialTask={focusTask}
        agentFilter={agentFilter}
        onClose={() => setFocusOpen(false)}
        onRefetch={() => { qc.invalidateQueries({ queryKey: ["tasks-today"] }); qc.invalidateQueries({ queryKey: ["tasks-summary"] }); }}
      />
    </div>
  );
}
